/**
 * shareFormSlice
 *
 * Open/dirty state for the Share Config Form. The share form is NOT a map
 * artifact (no geometry, no map layer), so it stays out of the artifact
 * registry — but it IS a first-class participant in the global unsaved-changes
 * guard (`formConflictSlice`). Mirroring the artifact slice's form contract
 * (`formIsOpen` / `formIsDirty` / `closeForm`) lets `getOpenForms` enumerate it
 * uniformly, so opening another form or switching workspace closes / guards it
 * automatically.
 *
 * @module store/slices/shareFormSlice
 */
import type { ShareLink } from '@/types/share';
import type { GeoMarkupStore } from '../index';

// ===== SLICE INTERFACE =====

export interface ShareFormSlice {
  shareForm: {
    formIsOpen: boolean;
    formMode: 'create' | 'edit' | null;
    /** Full share entity being edited (fetched before opening); null in create mode. */
    editingShare: ShareLink | null;
    /** Whether the form holds unsaved user input. Synced from `useShareForm`. */
    formIsDirty: boolean;

    /** Open the form in create mode (routes through the unsaved-changes guard). */
    openCreate: () => void;
    /** Open the form in edit mode for a fetched share (routes through the guard). */
    openEdit: (share: ShareLink) => void;
    /** Close + reset the form. Direct (no guard) — used post-save and by the guard itself. */
    closeForm: () => void;
    setFormIsDirty: (value: boolean) => void;
  };
}

// ===== HELPER =====

/**
 * Open the share form in `mode` (carrying `share` for edit), routed through the
 * global unsaved-changes guard so a dirty competing form prompts first.
 */
function openShareForm(
  set: (fn: (state: ShareFormSlice) => void) => void,
  get: () => ShareFormSlice,
  mode: 'create' | 'edit',
  share: ShareLink | null
): void {
  (get() as unknown as GeoMarkupStore).formConflict.request(() => {
    set((state) => {
      state.shareForm.formIsOpen = true;
      state.shareForm.formMode = mode;
      state.shareForm.editingShare = share;
      state.shareForm.formIsDirty = false;
    });
  });
}

// ===== SLICE CREATOR =====

export const createShareFormSlice = (
  set: (fn: (state: ShareFormSlice) => void) => void,
  get: () => ShareFormSlice,
  _store: unknown
) => ({
  shareForm: {
    formIsOpen: false,
    formMode: null as 'create' | 'edit' | null,
    editingShare: null as ShareLink | null,
    formIsDirty: false,

    openCreate: () => openShareForm(set, get, 'create', null),

    openEdit: (share: ShareLink) => openShareForm(set, get, 'edit', share),

    closeForm: () => {
      if (!get().shareForm.formIsOpen) return;
      set((state) => {
        state.shareForm.formIsOpen = false;
        state.shareForm.formMode = null;
        state.shareForm.editingShare = null;
        state.shareForm.formIsDirty = false;
      });
    },

    setFormIsDirty: (value: boolean) => {
      if (get().shareForm.formIsDirty === value) return;
      set((state) => {
        state.shareForm.formIsDirty = value;
      });
    },
  },
});
